import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
    Cms,
    DELIVERYTYPES,
    RequestsUpdatePickupDeliveryRequestDto
} from '@impact/data';
import { firstValueFrom, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { arrowRightIcon } from '../../../../../../../icons';
import { CheckoutFlowService } from '../../../../../../checkout/checkout-flow.service';
import { CheckoutService } from '../../../../../../checkout/checkout.service';
import { GlobalDataService } from '../../../../../../core/global-data.service';
import {
    getBusinessHoursClosesAtInt,
    getBusinessHoursOpensAtInt,
    getDateObjFromDate,
    getTimeFromUTCDate,
    getUTCDateString,
} from '../../../../../../core/helpers';
import { IDatePickerFormat } from '../../../../../../form-elements/datepicker/datepicker.component';
import { FormService } from '../../../../../../form/form.service';
import { DealershipDepartments } from '../../../../../../models/dealerships';

@Component({
    selector: 'app-checkout-step-delivery-tab-pickup',
    template: `
        <div *ngIf="form">
            <div class="tab-group__content-wrapper">
                <form class="flow__form" [formGroup]="form" (ngSubmit)="onSubmit()">
                    <section class="flow__form-section">
                        <div *ngIf="globalDataIsLoading; else shopInfo" class="loader__wrapper--non-absolute loader__wrapper--non-absolute--low">
                            <div class="loader"></div>
                        </div>
                        <ng-template #shopInfo>
                            <p class="text-small">{{ 'checkout.delivery_step____pickup_tab____shop_headline' | translate }}</p>
                            <h6>{{ dealership?.displayName }}</h6>
                            <p>{{ dealership?.address }}</p>
                        </ng-template>
                    </section>

                    <section class="flow__form-section">
                        <p>{{ 'checkout.delivery_step____pickup_tab____pick_up_date_label' | translate }}</p>
                        <app-datepicker [parentForm]="form" controlName="date" [label]="'Dato'" [daysToDelayMinDate]="4"></app-datepicker>
                    </section>

                    <section class="flow__form-section" *ngIf="showTimeslots && isOpen">
                        <p>{{ 'checkout.delivery_step____pickup_tab____pick_up_time_form_label' | translate }}</p>
                        <app-time-slot-picker
                            [currentSlot]="timeSlot"
                            [startHour]="timeSlotStartHour"
                            [endHour]="timeSlotEndHour"
                            [currentSlot]="timeSlot"
                            (handleClick)="handleSlotClick($event)"
                            [hasErrors]="timeSlotInvalid"
                        ></app-time-slot-picker>
                    </section>

                    <section *ngIf="isOpen !== undefined && !isOpen" class="flow__form-section state state--info">
                        <p>{{ 'checkout.delivery_step____pickup_tab____no_time_slots_on_chosen_date' | translate }}</p>
                    </section>

                    <div class="center grid-spacing-normal">
                        <app-button
                            *ngIf="!(checkoutFlowService.atFlowStart$ | async)"
                            buttonClass="button--tertiary button--has-spacing button--has-bottom-spacing"
                            [title]="'checkout.button____go_back_to_previous_step' | translate"
                            (handleClick)="checkoutFlowService.prevStep()"
                        ></app-button>
                        <app-button
                            buttonClass="button--primary button--has-spacing button--has-bottom-spacing"
                            buttonType="submit"
                            [loading]="!!(checkoutService.isCheckoutLoading$ | async)"
                            [title]="'buttons.continue' | translate"
                            [disabled]="isOpen !== undefined && !isOpen"
                        >
                            <i aria-hidden="true" class="button__icon">${arrowRightIcon}</i>
                        </app-button>
                    </div>
                </form>
            </div>
        </div>
    `,
})
export class CheckoutStepDeliveryTabPickupComponent implements OnInit, OnDestroy {
    private readonly unsubscribe = new Subject<void>();
    private openingHours: Cms.OpeningHourDto[] | undefined;
    private specialOpeningHours?: Cms.SpecialOpeningHourDto[] | undefined;
    
    form?: UntypedFormGroup;
    dealership: Cms.DealershipDto | undefined;
    timeSlot?: string;
    isOpen?: boolean;
    timeSlotStartHour?: number;
    timeSlotEndHour?: number;
    timeSlotInvalid?: boolean;
    showTimeslots?: boolean;
    globalDataIsLoading = true;

    constructor(
        private fb: UntypedFormBuilder,
        private globalDataService: GlobalDataService,
        public checkoutService: CheckoutService,
        public checkoutFlowService: CheckoutFlowService,
        private cd: ChangeDetectorRef
    ) {}
    async ngOnInit() {
        const { pickup, dealer } = await firstValueFrom(this.checkoutService.getCheckoutData());

        const pickupData = pickup;
        let date: Date | undefined;

        if (pickupData) {
            const { dateTime } = pickupData;
            if (dateTime) {
                date = new Date(dateTime);
                this.timeSlot = getTimeFromUTCDate(date);
            }
        }

        this.form = this.fb.group(
            {
                date: [(date && getDateObjFromDate(date)) || '', Validators.required],
            },
            {
                updateOn: 'change',
            }
        );
        this.cd.markForCheck();

        this.form.controls['date'].valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            this.showTimeslots = !!data;
            if (data) {
                this.setOpeningHours(data);
                this.timeSlot = undefined; // reset currently picked timeslot, if date change
            }
        });

        this.globalDataService
            .getDealerships()
            .pipe(first())
            .subscribe(
                (dealerships) => {
                    if (dealerships.children) {
                        this.dealership = dealerships?.children?.find(({ dealershipID }) => dealershipID === `${dealer?.dealerId}`);
                        const department = this.dealership?.departments?.find(
                            ({ departmentType }: any) => departmentType === DealershipDepartments.SALES
                        );

                        this.openingHours = department?.openingHours;
                        this.specialOpeningHours = department?.specialOpeningHours;

                        if (date) {
                            this.showTimeslots = true;
                            this.setOpeningHours(getDateObjFromDate(date));
                        }

                        this.globalDataIsLoading = false;
                        this.cd.markForCheck();
                    }
                },
                (_error) => {
                    this.globalDataIsLoading = false;
                }
            );
    }

    setOpeningHours(dateObj: IDatePickerFormat) {
        const dateString = getUTCDateString(dateObj);

        const todayIs = new Date(dateString).getDay();
        const today = this.specialOpeningHours?.find((s) => s.date === dateString) || this.openingHours?.[todayIs === 0 ? 6 : todayIs - 1]; // Because sunday is 0 in getDay(), but last in array from Umbraco.
        this.isOpen = today?.isOpen === undefined ? true : today?.isOpen;
        this.timeSlotStartHour = getBusinessHoursOpensAtInt(today?.hoursOfBusiness) || 9;
        this.timeSlotEndHour = getBusinessHoursClosesAtInt(today?.hoursOfBusiness) || 18;
    }

    handleSlotClick(slot: string) {
        this.timeSlot = slot;
        this.timeSlotInvalid = false;
    }

    onSubmit() {
        if (!this.timeSlot) {
            this.timeSlotInvalid = true;
        }

        if (this.form?.invalid || this.form?.disabled || (this.form && this.timeSlotInvalid)) {
            FormService.markControlsAsTouched(this.form);
            return;
        }

        const { date }: { [key: string]: string } = this.form?.value;

        const { year, month, day } = (date as unknown) as IDatePickerFormat;

        const dateTime = new Date(getUTCDateString({ year, month, day, time: this.timeSlot })).toISOString();

        const reqBody: RequestsUpdatePickupDeliveryRequestDto = {
            deliveryType: DELIVERYTYPES.Pickup,
            pickup: {
                dateTime,
            },
        };

        this.checkoutService.setPickupDelivery(reqBody);
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
